import React, { useState, useEffect } from 'react';
import { optionsService } from '../services/optionsService.ts';

interface ApiKeys {
    claude_api_key: string;
    gemini_api_key: string;
    openai_api_key: string;
    core_api_key: string;
    deepseek_api_key: string;
}

interface QuickReviewReason {
    id: number;
    text: string;
    type: 'accept' | 'reject';
}

export const OptionsPage: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<ApiKeys>({
        claude_api_key: '',
        gemini_api_key: '',
        openai_api_key: '',
        core_api_key: '',
        deepseek_api_key: '',
    });

    const [quickReasons, setQuickReasons] = useState<QuickReviewReason[]>([]);
    const [newReasonText, setNewReasonText] = useState('');
    const [newReasonType, setNewReasonType] = useState<'accept' | 'reject'>('accept');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchApiKeys();
        fetchQuickReasons();
    }, []);

    const fetchApiKeys = async () => {
        try {
            const keys = await optionsService.getApiKeys();
            setApiKeys(keys);
        } catch (error) {
            console.error('Error fetching API keys:', error);
        }
    };

    const fetchQuickReasons = async () => {
        try {
            const reasons = await optionsService.getQuickReasons();
            setQuickReasons(reasons);
        } catch (error) {
            console.error('Error fetching quick reasons:', error);
        }
    };

    const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setApiKeys((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const saveApiKeys = async () => {
        setIsLoading(true);
        try {
            await optionsService.updateApiKeys(apiKeys);
            setMessage('API keys saved successfully');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error saving API keys:', error);
            setMessage('Error saving API keys');
        } finally {
            setIsLoading(false);
        }
    };

    const addQuickReason = async () => {
        if (!newReasonText.trim()) return;

        try {
            const newReason = await optionsService.createQuickReason(newReasonText, newReasonType);
            setQuickReasons([...quickReasons, newReason]);
            setNewReasonText('');
        } catch (error) {
            console.error('Error adding quick reason:', error);
        }
    };

    const deleteQuickReason = async (id: number) => {
        try {
            await optionsService.deleteQuickReason(id);
            setQuickReasons(quickReasons.filter((reason) => reason.id !== id));
        } catch (error) {
            console.error('Error deleting quick reason:', error);
        }
    };

    return (
        <div className="options-container">
            <h2>Options</h2>

            <div className="options-section">
                <h3>API Keys</h3>
                <div className="form-group">
                    <label htmlFor="claude_api_key">Claude API Key</label>
                    <input
                        type="password"
                        id="claude_api_key"
                        name="claude_api_key"
                        value={apiKeys.claude_api_key}
                        onChange={handleApiKeyChange}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="gemini_api_key">Gemini API Key</label>
                    <input
                        type="password"
                        id="gemini_api_key"
                        name="gemini_api_key"
                        value={apiKeys.gemini_api_key}
                        onChange={handleApiKeyChange}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="openai_api_key">OpenAI API Key</label>
                    <input
                        type="password"
                        id="openai_api_key"
                        name="openai_api_key"
                        value={apiKeys.openai_api_key}
                        onChange={handleApiKeyChange}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="core_api_key">CORE API Key</label>
                    <input
                        type="password"
                        id="core_api_key"
                        name="core_api_key"
                        value={apiKeys.core_api_key}
                        onChange={handleApiKeyChange}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="deepseek_api_key">DeepSeek API Key</label>
                    <input
                        type="password"
                        id="deepseek_api_key"
                        name="deepseek_api_key"
                        value={apiKeys.deepseek_api_key}
                        onChange={handleApiKeyChange}
                    />
                </div>

                <button onClick={saveApiKeys} disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save API Keys'}
                </button>
                {message && <div className="message">{message}</div>}
            </div>

            <div className="options-section">
                <h3>Quick Review Reasons</h3>

                <div className="quick-reasons-form">
                    <input
                        type="text"
                        value={newReasonText}
                        onChange={(e) => setNewReasonText(e.target.value)}
                        placeholder="Enter reason text"
                    />
                    <select
                        value={newReasonType}
                        onChange={(e) => setNewReasonType(e.target.value as 'accept' | 'reject')}>
                        <option value="accept">Accept</option>
                        <option value="reject">Reject</option>
                    </select>
                    <button onClick={addQuickReason}>Add</button>
                </div>

                <div className="quick-reasons-list">
                    <h4>Accept Reasons</h4>
                    <ul>
                        {quickReasons
                            .filter((reason) => reason.type === 'accept')
                            .map((reason) => (
                                <li key={reason.id}>
                                    {reason.text}
                                    <button onClick={() => deleteQuickReason(reason.id)}>Delete</button>
                                </li>
                            ))}
                    </ul>

                    <h4>Reject Reasons</h4>
                    <ul>
                        {quickReasons
                            .filter((reason) => reason.type === 'reject')
                            .map((reason) => (
                                <li key={reason.id}>
                                    {reason.text}
                                    <button onClick={() => deleteQuickReason(reason.id)}>Delete</button>
                                </li>
                            ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};
